import type { ApplyResult, ThrowResult } from '../../core/types';
import {
  animateNumber,
  formatDamage,
  formatGold,
  formatNumber,
  formatPlayClock,
  formatTimer,
} from '../format';
import { isMuted, setMuted } from '../audioPref';
import { onSpeedChange, speedBadgeText } from '../debug/speed';
import { enemyEffectLines } from '../combatApi';
import { setEnemyEffectsProvider } from '../enemyEffects';
import {
  abandonRun,
  hasAbandonRun,
  hasMinion,
  inferno,
  isStunned,
  minionEnabled,
  onGameEvent,
  playSeconds,
  primesQueue,
  setMinionEnabled,
} from '../gameApi';
import { overlayRoot } from '../columnScale';
import { icon } from '../icons';
import { registerEscape } from '../keyboard';
import {
  CHIP_ORDER,
  CRIT_MACHINE_NODE,
  CRIT_STREAK_NODE,
  SPELL_STREAK_NODE,
  FORTY_TWO_SPELL_IDS,
  NULL_SPELL_IDS,
  STORM_SPELL_IDS,
  bottomChips,
  carriesSecondsSource,
  carriesSpell,
  chipIconOf,
  critMachineDuration,
  formatEnemyCount,
  owns,
  readArmor,
  readCritMachine,
  readEnemyCount,
  readFortyTwoCopies,
  readNullHeld,
  readSecondsGained,
  readStormReduction,
  readStormRemaining,
  readStreaks,
  type BottomFacts,
  type ChipId,
} from '../hudTalents';
import { onGameEventWith } from '../abyssApi';
import { readMaterialFacts } from '../materialsApi';
import { mountTips } from '../tips';
import { toast } from '../toast';
import type { ScreenMount } from '../types';

/**
 * Accès tolérant aux méthodes du tractopelle (v0.26.0, A72-17) : le HUD reste
 * montable sur un cœur qui ne les livre pas (le bouton reste alors masqué).
 */
interface BackhoeApi {
  hasBackhoe?: () => boolean;
  backhoeCount?: () => number;
  isBackhoeArmed?: () => boolean;
  armBackhoe?: () => boolean;
  disarmBackhoe?: () => boolean;
}

const DANGER_THRESHOLD = 5;

/** Icône du bouton de son : haut-parleur, barré quand le son est coupé. */
function muteIcon(muted: boolean): string {
  return icon(muted ? 'soundOff' : 'sound');
}

export const mountCombatHud: ScreenMount = (container, ctx) => {
  const { game, battle } = ctx;
  const el = document.createElement('div');
  el.className = 'hud';
  // Charte F1 (v0.14.0, A50) : bandeau d'ardoise, jetons et boutons en pierre
  // facettée, icônes vectorielles à la place des émojis (⛁ 🔊 🏳️).
  el.innerHTML = `
    <div class="hud-top">
      <div class="hud-gold f-chip">${icon('gold')}<span class="num" data-role="gold">0</span></div>
      <div class="hud-center">
        <div class="hud-label f-chip" data-role="label">
          <span class="hud-label-main" data-role="label-main"></span>
          <span class="hud-count" data-role="enemy-count" hidden></span>
        </div>
      </div>
      <button type="button" class="f-ibtn hud-mute" data-action="mute" aria-label="Son" aria-pressed="${
        isMuted() ? 'false' : 'true'
      }">${muteIcon(isMuted())}</button>
      <button type="button" class="f-ibtn hud-abandon" data-action="abandon" aria-label="Abandonner la run" hidden>${icon(
        'flag',
      )}</button>
    </div>
    <div class="hud-clock" data-role="play-clock" hidden></div>
    <div class="hud-timer-wrap" data-role="timer-wrap"><div class="hud-timer" data-role="timer">0.0</div></div>
    <!-- v0.47.0 (A95-4) : Assommé, talents et secondes gagnées descendent en bas du cadre (\`hud-bottom\`) ; v0.49.0 (A98) : les effets de l'ennemi vivent dans sa bannière. -->
    <div class="hud-states">
      <!-- Premiers et Enfer sont dessinés par la vue de combat (jetons au-dessus du bac, flammes au sol) : pas de doublon dans le HUD DOM. -->
      <div class="hud-primes hud-canvas-dup" data-role="primes" hidden></div>
      <div class="hud-inferno hud-canvas-dup" data-role="inferno" hidden></div>
    </div>
  `;
  container.appendChild(el);

  const goldEl = el.querySelector<HTMLElement>('[data-role="gold"]');
  const labelEl = el.querySelector<HTMLElement>('[data-role="label-main"]');
  const countEl = el.querySelector<HTMLElement>('[data-role="enemy-count"]');
  const clockEl = el.querySelector<HTMLElement>('[data-role="play-clock"]');
  const timerEl = el.querySelector<HTMLElement>('[data-role="timer"]');
  const muteBtn = el.querySelector<HTMLButtonElement>('[data-action="mute"]');
  const abandonBtn = el.querySelector<HTMLButtonElement>('[data-action="abandon"]');
  const primesEl = el.querySelector<HTMLElement>('[data-role="primes"]');
  const infernoEl = el.querySelector<HTMLElement>('[data-role="inferno"]');

  // ── Larbin (COMMENTS v0.6.0 §1.1) ────────────────────────────────────
  // Posé **hors** du bandeau du HUD : le bouton vit en bas à gauche, par-dessus
  // le bac de lancer, là où le pouce gauche le trouve sans masquer le chrono.
  const minionEl = document.createElement('div');
  minionEl.className = 'minion-toggle';
  minionEl.hidden = true;
  minionEl.innerHTML = `
    <button type="button" class="minion-btn f-ibtn" data-action="minion" aria-pressed="false">
      ${icon('minion', { className: 'minion-icon' })}
      <span class="sr-only">Larbin</span>
    </button>
    <p class="minion-caption f-cut" data-role="minion-caption"></p>
  `;
  container.appendChild(minionEl);
  const minionBtn = minionEl.querySelector<HTMLButtonElement>('[data-action="minion"]');
  const minionCaption = minionEl.querySelector<HTMLElement>('[data-role="minion-caption"]');

  // ── Tractopelle (COMMENTS v0.25.0 §1.3, A72-17) ──────────────────────
  // À côté du jeton du Larbin, visible seulement quand un *Dé du chef de
  // chantier* est équipé. Le bouton **arme** le prochain lancer normal (manuel,
  // auto ou Larbin) ; un second toucher le désarme. Il affiche le compteur.
  const backhoeEl = document.createElement('div');
  backhoeEl.className = 'backhoe-toggle';
  backhoeEl.hidden = true;
  backhoeEl.innerHTML = `
    <button type="button" class="backhoe-btn f-ibtn" data-action="backhoe" aria-pressed="false">
      ${icon('backhoe', { className: 'backhoe-icon' })}
      <span class="backhoe-count num" data-role="backhoe-count">0</span>
    </button>
    <p class="backhoe-caption f-cut" data-role="backhoe-caption">Lancer de tractopelle</p>
  `;
  container.appendChild(backhoeEl);
  const backhoeBtn = backhoeEl.querySelector<HTMLButtonElement>('[data-action="backhoe"]');
  const backhoeCountEl = backhoeEl.querySelector<HTMLElement>('[data-role="backhoe-count"]');
  const backhoeCaption = backhoeEl.querySelector<HTMLElement>('[data-role="backhoe-caption"]');
  const backhoeApi = game as unknown as BackhoeApi;

  // ── Double coup de Guinsoo (A72-15) : chiffres des deux coups ─────────
  const hitsLayer = document.createElement('div');
  hitsLayer.className = 'hud-hits';
  hitsLayer.setAttribute('aria-hidden', 'true');
  container.appendChild(hitsLayer);

  // ── Rangée du bas (A88-6, A95-4) ─────────────────────────────────────
  // Les indicateurs de combat — Assommé, Machine
  // Critique, Criticité, Flux arcanique, secondes gagnées — et les puces des
  // Matériaux vivent en **bas du cadre**, à la hauteur des boutons Larbin et
  // Tractopelle et à leur droite (jamais dessus), au-dessus de la barre de
  // temps de retour du bac. Chaque puce porte l'icône de son nœud dans l'arbre
  // et un libellé court ; elle n'existe à l'écran que si son talent ou son
  // bonus est actif. Le DOM est créé une fois (toutes les puces possibles,
  // masquées) : un rendu ne réécrit que les textes, états et jauges qui ont
  // changé (le chrono relit tout 60 fois par seconde). Depuis la v0.49.0 (A98)
  // les puces passent à la ligne (jamais de défilement) et la rangée grandit
  // vers le haut ; elle ne capte jamais le toucher.
  const bottomEl = document.createElement('div');
  bottomEl.className = 'hud-bottom';
  bottomEl.setAttribute('data-role', 'hud-bottom');
  bottomEl.dataset.side = '0';
  bottomEl.dataset.lines = '0';
  // Aucun blanc entre les balises : le texte de la puce est exactement son libellé.
  bottomEl.innerHTML = `<div class="hud-bottom-row" data-role="hud-bottom-row">${CHIP_ORDER.map(
    (id) =>
      `<div class="hud-bchip f-cut" data-role="hud-chip" data-chip="${id}" data-state="off" role="img" hidden>${icon(
        chipIconOf(id),
      )}<span class="hud-bchip-text"></span>${
        id === 'critmachine' ? '<span class="hud-gauge"><i class="hud-gauge-fill"></i></span>' : ''
      }</div>`,
  ).join('')}</div>`;
  container.appendChild(bottomEl);

  interface ChipSlot {
    el: HTMLElement;
    text: HTMLElement;
    label: string;
    full: string;
    state: string;
    gauge: string;
    material: string;
  }
  const chipSlots = new Map<ChipId, ChipSlot>();
  for (const id of CHIP_ORDER) {
    const chipEl = bottomEl.querySelector<HTMLElement>(`[data-chip="${id}"]`);
    const text = chipEl?.querySelector<HTMLElement>('.hud-bchip-text');
    if (chipEl && text) chipSlots.set(id, { el: chipEl, text, label: '', full: '', state: 'off', gauge: '', material: '' });
  }

  function renderLabel(): void {
    if (!labelEl) return;
    const s = game.state;
    if (s.phase === 'invasion') {
      if (s.enemy?.isBoss) {
        labelEl.textContent = 'Boss !';
      } else {
        labelEl.textContent = `Invasion · Ennemi ${Math.min(s.enemyIndex + 1, s.enemiesTotal)}/${s.enemiesTotal}`;
      }
    } else {
      labelEl.textContent = `Jour ${s.day}/5 · Niveau ${s.level}`;
    }
    renderEnemyCount();
  }

  /**
   * Compteur d'ennemis (A88-4) : seconde ligne du jeton du haut — Expédition
   * « Tués 37 / 50 » (plafond du cœur), Invasion « Tués 3 / 5 ». Masqué tant que
   * le cœur ne publie pas le plafond de l'Expédition.
   */
  let lastCount = '';
  function renderEnemyCount(): void {
    if (!countEl) return;
    const c = readEnemyCount(game);
    const text = c ? `Tués ${formatEnemyCount(c.done, c.total)}` : '';
    if (text === lastCount) return;
    lastCount = text;
    countEl.hidden = !c;
    countEl.textContent = text;
  }

  /**
   * Faits lus à chaque rendu de la rangée du bas : le niveau des nœuds est relu
   * (un achat de debug doit se voir sans remonter le HUD) et tout ce que le cœur
   * ne publie pas encore retombe sur une valeur neutre.
   */
  function readBottomFacts(): BottomFacts {
    const streaks = readStreaks(game);
    const machine = readCritMachine(game);
    const gained = readSecondsGained(game);
    // A98 : Tempête, Null, Armure et 42 se lisent dynamiquement ; sans le cœur, la puce se tait.
    const storm = readStormRemaining(game);
    const nullHeld = readNullHeld(game);
    const armor = readArmor(game);
    const copies = readFortyTwoCopies(game);
    return {
      stunned: isStunned(game),
      machine: {
        owned: owns(game, CRIT_MACHINE_NODE),
        remaining: machine?.remaining ?? 0,
        factor: machine?.factor ?? 1,
        duration: critMachineDuration(),
      },
      critStreak: { owned: owns(game, CRIT_STREAK_NODE), streak: streaks.critStreak, mult: streaks.critMult },
      spellStreak: { owned: owns(game, SPELL_STREAK_NODE), streak: streaks.spellStreak, mult: streaks.spellMult },
      seconds: { visible: gained !== null && carriesSecondsSource(game), gained: gained ?? 0 },
      storm: {
        visible: storm !== null && (storm > 0 || carriesSpell(game, STORM_SPELL_IDS)),
        remaining: storm ?? 0,
        reduction: readStormReduction(game),
      },
      nullMark: { visible: nullHeld !== null || carriesSpell(game, NULL_SPELL_IDS), held: nullHeld },
      armor: { visible: armor.present, stacks: armor.stacks, perHit: armor.perHit },
      fortyTwo: {
        visible: copies !== null && (copies > 0 || carriesSpell(game, FORTY_TWO_SPELL_IDS)),
        copies: copies ?? 0,
      },
      materials: readMaterialFacts(game),
    };
  }

  /**
   * Nombre de lignes de la rangée (A98) : les puces passent à la ligne au lieu de
   * défiler ; la rangée est ancrée par le bas, elle grandit donc vers le haut et
   * ne descend jamais sur la barre de temps de retour ni sur l'invite de lancer.
   * `data-lines` (1, 2, 3…) le dit à la feuille de style et aux tests ; 0 sans
   * aucune puce. Une ligne = une valeur distincte de `offsetTop` parmi les puces
   * visibles.
   */
  function updateLines(): void {
    const tops = new Set<number>();
    for (const slot of chipSlots.values()) if (!slot.el.hidden) tops.add(slot.el.offsetTop);
    const lines = String(tops.size);
    if (bottomEl.dataset.lines !== lines) bottomEl.dataset.lines = lines;
  }

  function renderBottom(): void {
    const views = new Map(bottomChips(readBottomFacts()).map((v) => [v.chip as ChipId, v]));
    let layoutChanged = false;
    for (const id of CHIP_ORDER) {
      const slot = chipSlots.get(id);
      if (!slot) continue;
      const view = views.get(id);
      if (!view) {
        if (!slot.el.hidden) {
          slot.el.hidden = true;
          layoutChanged = true;
        }
        continue;
      }
      if (slot.el.hidden) {
        slot.el.hidden = false;
        layoutChanged = true;
      }
      if (slot.label !== view.label) {
        slot.label = view.label;
        slot.text.textContent = view.label;
        layoutChanged = true;
      }
      if (slot.full !== view.full) {
        slot.full = view.full;
        slot.el.setAttribute('aria-label', view.full);
        slot.el.setAttribute('title', view.full);
      }
      if (slot.state !== view.state) {
        slot.state = view.state;
        slot.el.dataset.state = view.state;
      }
      const material = view.material ?? '';
      if (slot.material !== material) {
        slot.material = material;
        if (material) slot.el.dataset.material = material;
        else delete slot.el.dataset.material;
      }
      if (view.fill !== undefined) {
        const gauge = view.fill.toFixed(3);
        if (slot.gauge !== gauge) {
          slot.gauge = gauge;
          slot.el.style.setProperty('--gauge', gauge);
        }
      }
    }
    if (layoutChanged) updateLines();
  }

  /**
   * Place laissée à gauche par les boutons Larbin et Tractopelle (0, 1 ou 2
   * boutons) : la rangée du bas commence à leur droite, sans les recouvrir.
   */
  function renderSide(): void {
    const side = String((minionEl.hidden ? 0 : 1) + (backhoeEl.hidden ? 0 : 1));
    if (bottomEl.dataset.side === side) return;
    bottomEl.dataset.side = side;
    updateLines();
  }

  /**
   * Chrono de partie (COMMENTS v0.6.0 §2) : le temps cumulé depuis la création
   * de la sauvegarde, à côté du jeton jour/niveau. Masqué tant que le cœur ne
   * publie pas `state.stats.playSeconds` — un « 00:00:00 » figé mentirait.
   */
  function renderClock(): void {
    if (!clockEl) return;
    const seconds = playSeconds(game);
    clockEl.hidden = seconds === null;
    if (seconds === null) return;
    clockEl.textContent = `Temps de jeu ${formatPlayClock(seconds)}`;
  }

  /**
   * Pastille de vitesse de debug (A99) : « ×4 », « ×4 · sans vol » ou « sans
   * vol », à côté du chrono tant qu'un de ces réglages est actif. L'élément
   * n'existe pas dans le DOM sinon.
   */
  const timerWrap = el.querySelector<HTMLElement>('[data-role="timer-wrap"]');
  let speedBadge: HTMLElement | null = null;
  function renderSpeedBadge(): void {
    const text = speedBadgeText();
    if (text === null) {
      speedBadge?.remove();
      speedBadge = null;
      return;
    }
    if (!speedBadge) {
      speedBadge = document.createElement('span');
      speedBadge.className = 'hud-speed';
      speedBadge.setAttribute('data-role', 'speed-badge');
      timerWrap?.appendChild(speedBadge);
    }
    if (speedBadge.textContent !== text) speedBadge.textContent = text;
  }
  renderSpeedBadge();
  const offSpeed = onSpeedChange(renderSpeedBadge);

  /**
   * File de *Premiers* (§1.2.2) : « Premiers ×2 → ×3 → ×5 ». Le premier jeton
   * est celui qui s'appliquera au prochain lancer ; la file se vide d'elle-même
   * au fil des lancers. Rien à l'écran tant qu'elle est vide.
   */
  // Signatures du dernier rendu : ces deux indicateurs sont relus à chaque
  // `timer` (soit 60 fois par seconde) parce qu'ils peuvent changer sur un
  // lancer du Larbin comme sur un lancer manuel. Comparer une courte chaîne
  // évite de reconstruire le balisage pour rien à chaque image.
  let lastPrimes = '';
  let lastInferno = '';

  function renderPrimes(): void {
    if (!primesEl) return;
    const queue = primesQueue(game);
    const signature = queue.join(',');
    if (signature === lastPrimes) return;
    lastPrimes = signature;
    primesEl.hidden = queue.length === 0;
    if (queue.length === 0) {
      primesEl.innerHTML = '';
      return;
    }
    primesEl.innerHTML = `<span class="hud-primes-label">Premiers</span>${queue
      .map(
        (m, i) =>
          `<span class="hud-chip hud-chip--prime${i === 0 ? ' hud-chip--next' : ''}">×${formatNumber(m)}</span>`,
      )
      // Séparateur en ASCII (historique, DECISIONS §D.13) : la police pixel de
      // l'époque n'avait pas de « → » ; les polices F1 non plus hors latin.
      .join('<span class="hud-chip-sep" aria-hidden="true">&gt;</span>')}`;
  }

  /** État *666 · ENFER* (§1.2.2) : actif jusqu'à la fin de l'Expédition. */
  function renderInferno(): void {
    if (!infernoEl) return;
    const { active, multiplier } = inferno(game);
    const signature = active ? String(multiplier) : '';
    if (signature === lastInferno) return;
    lastInferno = signature;
    infernoEl.hidden = !active;
    if (!active) return;
    infernoEl.textContent = `666 · ENFER ×${formatNumber(multiplier)}`;
  }

  /**
   * Bouton *Larbin* : visible seulement quand le talent est débloqué
   * (`game.hasMinion()`), allumé quand il lance, éteint sinon. La légende
   * rappelle le malus permanent du serviteur (−25 % de dégâts).
   */
  function renderMinion(): void {
    const owned = hasMinion(game);
    minionEl.hidden = !owned;
    if (!owned) {
      renderBackhoe();
      return;
    }
    const on = minionEnabled(game);
    minionEl.classList.toggle('minion-toggle--on', on);
    minionEl.classList.toggle('minion-toggle--off', !on);
    minionBtn?.setAttribute('aria-pressed', on ? 'true' : 'false');
    minionBtn?.setAttribute('aria-label', on ? 'Larbin actif' : 'Larbin en pause');
    if (minionCaption) {
      // *Esclavagisme* (A72-12) relève le facteur : on lit celui du cœur.
      const factor = (game.getDerivedStats() as { minionDamageFactor?: number }).minionDamageFactor ?? 0.75;
      minionCaption.textContent = on
        ? `Larbin actif · lance à votre place · ×${formatNumber(factor)} dégâts`
        : 'Larbin en pause';
    }
    renderBackhoe();
  }

  /**
   * Bouton *Lancer de tractopelle* : visible avec un *Dé du chef de chantier*
   * équipé, compteur de dés en réserve, état « armé ». Placé à droite du
   * Larbin quand celui-ci est là, à sa place sinon.
   */
  let lastBackhoe = '';
  function renderBackhoe(): void {
    const owned = typeof backhoeApi.hasBackhoe === 'function' && backhoeApi.hasBackhoe.call(game);
    const count = owned ? (backhoeApi.backhoeCount?.call(game) ?? 0) : 0;
    const armed = owned && backhoeApi.isBackhoeArmed?.call(game) === true;
    const signature = owned ? `${count}:${armed}:${minionEl.hidden}` : '';
    if (signature === lastBackhoe) {
      // Rien ne change côté Tractopelle, mais le Larbin a pu apparaître ou partir.
      renderSide();
      return;
    }
    lastBackhoe = signature;
    backhoeEl.hidden = !owned;
    renderSide();
    if (!owned) return;
    backhoeEl.classList.toggle('backhoe-toggle--beside', !minionEl.hidden);
    backhoeEl.classList.toggle('backhoe-toggle--armed', armed);
    backhoeEl.classList.toggle('backhoe-toggle--empty', count <= 0 && !armed);
    if (backhoeCountEl) backhoeCountEl.textContent = formatGold(count);
    backhoeBtn?.setAttribute('aria-pressed', armed ? 'true' : 'false');
    backhoeBtn?.setAttribute(
      'aria-label',
      armed ? `Tractopelle armé : ${formatGold(count)} dés` : `Lancer de tractopelle : ${formatGold(count)} dés`,
    );
    if (backhoeCaption) {
      backhoeCaption.textContent = armed
        ? `Tractopelle armé · +${formatGold(count)} dés au prochain lancer`
        : count > 0
          ? `Lancer de tractopelle · ${formatGold(count)} dés`
          : 'Tractopelle vide · se charge à chaque lancer';
    }
  }

  /**
   * Chiffres du **double coup** de *Guinsoo* (A72-15) : un chiffre par coup,
   * le second légèrement décalé (à droite, plus bas, un peu plus tard), teinte
   * « crit » quand le lancer est critique, « damage » sinon. Les lancers à un
   * seul coup gardent les chiffres de la vue de combat.
   */
  function showGuinsooHit(hit: number, damage: number, crit: boolean): void {
    const n = document.createElement('div');
    n.className = `hud-hit hud-hit--${crit ? 'crit' : 'damage'}${hit > 1 ? ' hud-hit--second' : ''}`;
    n.setAttribute('data-role', 'guinsoo-hit');
    n.setAttribute('data-hit', String(hit));
    n.textContent = hit > 1 ? `${formatDamage(damage)} ×2` : formatDamage(damage);
    hitsLayer.appendChild(n);
    window.setTimeout(() => n.remove(), 1300);
  }

  function renderTimer(remaining: number): void {
    if (!timerEl) return;
    timerEl.textContent = formatTimer(remaining);
    const danger = remaining < DANGER_THRESHOLD;
    timerEl.classList.toggle('hud-timer--danger', danger);
  }

  if (goldEl) goldEl.textContent = formatGold(game.state.pendingGold);
  if (abandonBtn) abandonBtn.hidden = !hasAbandonRun(game);
  renderLabel();
  renderClock();
  renderTimer(game.state.timeRemaining);
  renderPrimes();
  renderInferno();
  renderMinion();
  renderBackhoe();
  renderBottom();

  const offApply = game.on('apply', (payload: ApplyResult & { result: ThrowResult }) => {
    if (payload.goldGained > 0 && goldEl) {
      const to = game.state.pendingGold;
      const from = Math.max(0, to - payload.goldGained);
      animateNumber(goldEl, from, to);
    }
    renderLabel();
    renderPrimes();
    renderInferno();
    // Le talent peut avoir été acheté par le panneau de debug sans que le HUD
    // soit remonté (la phase n'a pas changé).
    renderMinion();
    renderBottom();
  });
  // Coups portés (A72-15) : seuls les lancers à **deux** coups ont leurs
  // chiffres dans le HUD ; le premier sort aussitôt, le second avec un léger
  // retard pour que les deux se lisent.
  const offHit = game.on('hit', ({ result, hit, damage, crit }) => {
    if (result.hits !== 2) return;
    if (hit > 1) window.setTimeout(() => showGuinsooHit(hit, damage, crit), 160);
    else showGuinsooHit(hit, damage, crit);
  });
  const offBackhoe = game.on('backhoe', () => renderBackhoe());
  const offSpawn = game.on('spawn', () => {
    renderLabel();
    // Un nouvel ennemi porte des effets différents (voire aucun), intact ou brisé,
    // ses effets annulés ou non, son armure et sa marque Null remises à zéro — et
    // le Serpent repart à zéro coup.
    renderBottom();
  });
  // Événement `material` du cœur (A95) : Boisé, Osseux, Silicium… ; absent d'un
  // cœur qui ne le livre pas (l'abonnement est alors sans effet).
  const offMaterial = onGameEventWith(game, 'material', () => renderBottom());
  // Marque de Null (A98) : posée, grossie par chaque lancer retenu, libérée ou perdue.
  const offNullMark = onGameEventWith(game, 'nullMark', () => renderBottom());
  const offTimer = game.on('timer', ({ remaining }) => {
    renderTimer(remaining);
    // *Premiers* et l'Enfer changent aussi sur un lancer du Larbin, qui n'est
    // pas toujours annoncé par `apply` : on les relit au rythme du chrono, sans
    // reconstruire quoi que ce soit tant que rien n'a bougé. L'assommement suit
    // le même rythme : `stunRemaining` décroît à chaque tick.
    renderPrimes();
    renderInferno();
    renderBackhoe();
    // La jauge de Machine Critique se vide à chaque image du chrono.
    renderBottom();
  });
  // Le Larbin lance de son côté : son jeton pulse à chaque lancer (événement
  // détecté à l'exécution — il n'existe pas tant que le cœur ne l'émet pas).
  const offMinionThrow = onGameEvent(game, 'minionThrow', () => {
    if (minionEl.hidden) return;
    minionEl.classList.remove('minion-toggle--throw');
    // Redémarre l'animation : sans relecture forcée, deux lancers rapprochés
    // n'en jouent qu'une seule.
    void minionEl.offsetWidth;
    minionEl.classList.add('minion-toggle--throw');
    renderPrimes();
  });

  /**
   * Chrono de partie : une seconde de pas. Il ne fait **pas** avancer le temps
   * (c'est `game.tick()` pendant le combat et `addMenuTime` hors combat) : il
   * ne fait que relire ce que le cœur publie.
   */
  const clockTimer = window.setInterval(() => {
    if (typeof document !== 'undefined' && document.hidden) return;
    renderClock();
  }, 1000);

  // La rangée change de largeur ou de hauteur sans que le HUD le sache (police
  // chargée après coup, fenêtre redimensionnée, bouton Larbin qui apparaît, puce
  // qui passe à la ligne) : on la surveille pour tenir `data-lines` à jour.
  const bottomObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(() => updateLines()) : null;
  bottomObserver?.observe(bottomEl);
  const bottomRow = bottomEl.querySelector<HTMLElement>('[data-role="hud-bottom-row"]');
  if (bottomRow) bottomObserver?.observe(bottomRow);

  const onMute = (): void => {
    const next = !isMuted();
    setMuted(next);
    battle.setMuted?.(next);
    if (muteBtn) {
      muteBtn.innerHTML = muteIcon(next);
      // Bouton bascule « Son » : enfoncé tant que le son joue.
      muteBtn.setAttribute('aria-pressed', next ? 'false' : 'true');
    }
  };
  muteBtn?.addEventListener('click', onMute);

  const onMinion = (): void => {
    setMinionEnabled(game, !minionEnabled(game));
    renderMinion();
  };
  minionBtn?.addEventListener('click', onMinion);

  const onBackhoe = (): void => {
    if (backhoeApi.isBackhoeArmed?.call(game)) {
      backhoeApi.disarmBackhoe?.call(game);
    } else if (!backhoeApi.armBackhoe?.call(game)) {
      toast('Tractopelle vide : il se charge à chaque lancer.', 2000, 'backhoe');
    }
    renderBackhoe();
  };
  backhoeBtn?.addEventListener('click', onBackhoe);

  /**
   * Abandon de run (A30) : confirmation obligatoire — un toucher accidentel du
   * bouton, discret dans le bandeau, ne doit pas coûter la run. La modale ne
   * touche à aucun chrono ni pause : rien de tel n'existe dans le jeu, et cette
   * interface n'en invente pas (elle se contente de rester par-dessus, le
   * combat continuant derrière tant que le joueur n'a pas confirmé).
   */
  function openAbandonConfirm(): void {
    const overlay = document.createElement('div');
    overlay.className = 'f-scrim';
    // Modale F1 : panneau à filet d'or, deux blocs de pierre côte à côte.
    overlay.innerHTML = `
      <div class="f-modal" role="dialog" aria-modal="true" aria-labelledby="abandon-title">
        <h2 class="f-modal-title" id="abandon-title">Abandonner la run ?</h2>
        <p class="f-modal-text">L'or et les dés de la run sont perdus.</p>
        <div class="f-modal-actions">
          <button type="button" class="f-btn f-btn--danger" data-action="confirm-abandon">Abandonner</button>
          <button type="button" class="f-btn f-btn--ghost" data-action="cancel-abandon">Continuer</button>
        </div>
      </div>
    `;
    let unregisterEscape = (): void => {};
    const close = (): void => {
      unregisterEscape();
      overlay.remove();
    };
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) close();
    });
    overlay.querySelector('[data-action="cancel-abandon"]')?.addEventListener('click', close);
    overlay.querySelector('[data-action="confirm-abandon"]')?.addEventListener('click', () => {
      close();
      // `abandonRun()` fait passer la phase à `runLost` et émet `phase` : le
      // routeur (`ui/app.ts`) démonte ce HUD tout seul, rien d'autre à faire ici.
      abandonRun(game);
    });
    overlayRoot().appendChild(overlay);
    unregisterEscape = registerEscape(overlay, close);
    // Focus sur « Continuer » : au clavier, Entrée ne coûte jamais la run.
    overlay.querySelector<HTMLButtonElement>('[data-action="cancel-abandon"]')?.focus();
  }

  const onAbandon = (): void => openAbandonConfirm();
  abandonBtn?.addEventListener('click', onAbandon);

  // Info-bulle de première partie : l'invite de lancer (phase 4, `ui/tips.ts`).
  const offTips = mountTips(ctx, 'combat', el);

  // Les effets de l'ennemi vivent dans sa bannière (A98) : on lui publie leurs
  // libellés (une ligne par effet), relus par la vue de combat à chaque image.
  setEnemyEffectsProvider(() => enemyEffectLines(game));

  return () => {
    setEnemyEffectsProvider(null);
    offTips();
    offSpeed();
    offApply();
    offSpawn();
    offMaterial();
    offNullMark();
    offTimer();
    offMinionThrow();
    offHit();
    offBackhoe();
    window.clearInterval(clockTimer);
    bottomObserver?.disconnect();
    muteBtn?.removeEventListener('click', onMute);
    minionBtn?.removeEventListener('click', onMinion);
    backhoeBtn?.removeEventListener('click', onBackhoe);
    abandonBtn?.removeEventListener('click', onAbandon);
  };
};
